/**
 * Which product properties may be copied from one shop to the other.
 *
 * VCE and HVO are two storefronts on nearly the same products, picked from one freezer. A fact
 * about the physical product - how much a pick weighs, whether it is frozen, what is in it - is
 * true in both shops, and typing it twice is how the two drift apart. A fact about the *shop* -
 * the name on the page, the description, the price - is deliberately allowed to differ.
 *
 * So syncing is opt-in per property, per link (see `productLinks`): this registry is the full
 * set of properties that *may* be synced, and each link stores its own `syncedProperties`
 * array picked from it. `defaultOn` is what a fresh link starts with.
 *
 * Phase 1 only edits WAREHOUSE_FIELDS in this CMS - the rest of product management still lives
 * in `cms/`. The registry is deliberately wider than phase 1 so the full product editor can be
 * moved here later without redoing this list.
 *
 * A property added later carries `since`, the registry version it came with (see
 * effectiveSyncedProperties).
 */

/** Groups, used by the screen to order the checkbox list. */
export const SYNC_GROUP_WAREHOUSE = "warehouse";
export const SYNC_GROUP_PHYSICAL = "physical";
export const SYNC_GROUP_COMMERCIAL = "commercial";

export const SYNC_GROUP_LABELS = {
  [SYNC_GROUP_WAREHOUSE]: "Magazijn",
  [SYNC_GROUP_PHYSICAL]: "Producteigenschappen",
  [SYNC_GROUP_COMMERCIAL]: "Commercieel",
};

/**
 * The properties that may be synced between a linked VCE and HVO product.
 *
 * `type` tells the screen how to render a before/after value, and tells `productSync` how to
 * compare two values - `grams` in particular is stored as both a number and a numeric string
 * in Firestore (hence `Object` in api's FirestoreProduct.java) and has to be normalised first.
 */
export const SYNCABLE_PROPERTIES = [
  // --- Magazijn: the fields phase 1 actually edits -----------------------------------------
  {
    key: "pickWeightGram",
    label: "Inhoud / pickgewicht (gram)",
    group: SYNC_GROUP_WAREHOUSE,
    defaultOn: true,
    type: "grams",
  },
  {
    key: "volumeWeightGram",
    label: "Volumegewicht (gram)",
    group: SYNC_GROUP_WAREHOUSE,
    defaultOn: true,
    type: "grams",
  },
  {
    // How many units as the webshop sells them lie in one package in the freezer
    // (functions/packaging.js). Added for the scanner, which counts a whole package per scan.
    // This one is for the product without weight variants...
    key: "piecesPerPack",
    label: "Stuks per verpakking",
    group: SYNC_GROUP_WAREHOUSE,
    defaultOn: true,
    type: "pieces",
    since: 2,
  },
  {
    // ...and this one per weight: kogelbiefstuk of 125 gram lies in pairs, of 500 gram not
    key: "piecesPerPackByWeight",
    label: "Stuks per verpakking per gewicht",
    group: SYNC_GROUP_WAREHOUSE,
    defaultOn: true,
    type: "piecesByWeight",
    since: 2,
  },
  {
    key: "pickNote",
    label: "Picknotitie",
    group: SYNC_GROUP_WAREHOUSE,
    defaultOn: true,
    type: "text",
  },

  // --- Facts about the same piece of meat, true in both shops -----------------------------
  {
    key: "frozen",
    label: "Diepvries",
    group: SYNC_GROUP_PHYSICAL,
    defaultOn: true,
    type: "boolean",
  },
  {
    key: "taxRate",
    label: "Btw-tarief",
    group: SYNC_GROUP_PHYSICAL,
    defaultOn: true,
    type: "number",
  },
  {
    key: "nutritionalValue",
    label: "Voedingswaarden",
    group: SYNC_GROUP_PHYSICAL,
    defaultOn: true,
    type: "map",
  },
  {
    key: "ingredients",
    label: "Ingrediënten",
    group: SYNC_GROUP_PHYSICAL,
    defaultOn: true,
    type: "list",
  },
  {
    key: "traceability",
    label: "Herkomst / traceerbaarheid",
    group: SYNC_GROUP_PHYSICAL,
    defaultOn: true,
    type: "list",
  },

  // --- Commercial: copyable, but the two shops are allowed to differ, so off by default ----
  {
    key: "price",
    label: "Prijs",
    group: SYNC_GROUP_COMMERCIAL,
    defaultOn: false,
    type: "number",
  },
  {
    key: "discount",
    label: "Kortingsprijs",
    group: SYNC_GROUP_COMMERCIAL,
    defaultOn: false,
    type: "number",
  },
  {
    key: "min_amount",
    label: "Minimale afname",
    group: SYNC_GROUP_COMMERCIAL,
    defaultOn: false,
    type: "number",
  },
  {
    key: "orderUnit",
    label: "Besteleenheid",
    group: SYNC_GROUP_COMMERCIAL,
    defaultOn: false,
    type: "number",
  },
  {
    key: "onlyRegional",
    label: "Alleen regionaal",
    group: SYNC_GROUP_COMMERCIAL,
    defaultOn: false,
    type: "boolean",
  },
];

/**
 * Properties that are technically copyable but break, or are meaningless, on the other side.
 * Listed explicitly rather than just left out of SYNCABLE_PROPERTIES, so that a later addition
 * to the registry can be checked against a written-down "no" instead of someone's memory.
 */
export const NEVER_SYNC = [
  // Category ids come from the `categories` collection of that product's OWN Firebase project.
  // VCE and HVO are separate projects with separate category documents, so copying the ids
  // would point the HVO product at VCE document ids that do not exist there: the product falls
  // out of every category listing and the webshop renders an empty breadcrumb.
  "categories",
  // Image values are download URLs into that project's OWN Storage bucket. Copying the URL
  // leaves the HVO product rendering an image served from the VCE bucket: it breaks the moment
  // VCE's bucket rules change or the file is replaced, and an HVO page would be silently
  // hotlinking VCE's storage (and billing) in the meantime. Images must be re-uploaded.
  "image",

  // Shop-facing copy: the whole point is that the two storefronts may word things differently.
  "url",
  "name",
  "description",
  "shortDescription",
  "additionalInfo",
  "customTabs",

  // Per-shop bookkeeping: each shop counts its own stock and its own sales.
  "stock",
  "purchased",

  // Variants carry their own per-shop ids and pricing; copying them would mix up the two
  // shops' variant documents.
  "productVariants",
  "variantName",
  "hideVariant",

  // Merchandising, decided per storefront.
  "tag",
  "productOfTheWeek",
];

/** The fields phase 1 actually edits in this CMS. */
export const WAREHOUSE_FIELDS = ["pickWeightGram", "volumeWeightGram", "piecesPerPack", "piecesPerPackByWeight", "pickNote"];

const PROPERTY_BY_KEY = new Map(SYNCABLE_PROPERTIES.map((property) => [property.key, property]));
const NEVER_SYNC_KEYS = new Set(NEVER_SYNC);

export const SYNCABLE_PROPERTY_KEYS = SYNCABLE_PROPERTIES.map((property) => property.key);

/** What a freshly created link starts with: the physical facts, not the commercial ones. */
export const DEFAULT_SYNCED_PROPERTIES = SYNCABLE_PROPERTIES.filter((property) => property.defaultOn).map(
  (property) => property.key,
);

/** The registry entry for a key, or null. The single way to look one up. */
export const getSyncableProperty = (key) => PROPERTY_BY_KEY.get(key) || null;

/**
 * The gate: anything not in the registry - unknown, misspelled, or explicitly on NEVER_SYNC -
 * is refused here, so nothing outside the registry can ever be copied between shops.
 */
export const isSyncableProperty = (key) => !NEVER_SYNC_KEYS.has(key) && PROPERTY_BY_KEY.has(key);

export const isNeverSyncProperty = (key) => NEVER_SYNC_KEYS.has(key);

/** Label for a key, falling back to the key itself so a screen never renders "undefined". */
export const syncPropertyLabel = (key) => (getSyncableProperty(key) || {}).label || key;

/**
 * Clean a stored `syncedProperties` array: drop anything no longer in the registry, drop
 * duplicates, and return it in registry order so the screen and the melding list properties
 * the same way every time.
 */
export const sanitiseSyncedProperties = (keys) => {
  const wanted = new Set((keys || []).filter(isSyncableProperty));
  return SYNCABLE_PROPERTY_KEYS.filter((key) => wanted.has(key));
};

/**
 * The registry's version, stored on a link next to its `syncedProperties` whenever that list is
 * written. Version 2 added piecesPerPack and piecesPerPackByWeight.
 */
export const SYNC_REGISTRY_VERSION = 2;

/**
 * What a stored link really syncs: its own list, plus every property that is on by default and was
 * added to the registry after the link's list was written.
 *
 * Those links never had the choice - the property did not exist yet - and treating it as switched
 * off would leave it to someone to tick it for each of a few hundred links by hand. Once the list
 * is saved again it carries the current version, and from then on it holds exactly what it says.
 */
export const effectiveSyncedProperties = (link) => {
  const stored = (link && link.syncedProperties) || [];
  const version = Number(link && link.syncedPropertiesVersion) || 1;
  const added = SYNCABLE_PROPERTIES.filter((property) => property.defaultOn && (property.since || 1) > version).map(
    (property) => property.key,
  );
  return sanitiseSyncedProperties([...stored, ...added]);
};

/** Registry entries grouped for rendering, in registry order. */
export const syncablePropertiesByGroup = () =>
  [SYNC_GROUP_WAREHOUSE, SYNC_GROUP_PHYSICAL, SYNC_GROUP_COMMERCIAL].map((group) => ({
    group,
    label: SYNC_GROUP_LABELS[group],
    properties: SYNCABLE_PROPERTIES.filter((property) => property.group === group),
  }));
